import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "react-redux";
import { useJoyride } from "react-joyride";
import { TourContext } from "../../context/TourContext";
import { TourTooltip } from "./TourTooltip";
import { TourArrow } from "./TourArrow";
import { TOUR_REGISTRY } from "./tourRegistry";
import TourSkipModal from "./TourSkipModal";
import TourFinishModal from "./TourFinishModal";
import axiosInstance from "../../config/axiosConfig/axiosConfig";
import { BRAND } from "../../utils/constants/colors";


export default function GuidedTourProvider({ children }) {
  const navigate = useNavigate();
  const store = useStore();

  // stepRef.current is always the live step index — safe inside event handlers
  const stepRef = useRef(0);
  // pendingStartRef holds { fromStep } between setActiveSteps() and the effect that fires controls.start()
  const pendingStartRef = useRef(null);
  // Ref mirror of activeTourKey — always current inside event-handler closures.
  const activeTourKeyRef = useRef(null);

  // ── Pipeline refs (all mutable, no re-render needed) ──────────────────────
  // Each pipeline item: { key: string, fromStep: number, name: string }
  const pipelineRef = useRef([]);       // ordered list of tours to run
  const pipelineIdxRef = useRef(0);     // index of the currently running tour
  // Set to true when user explicitly skips/closes; prevents tour:end from
  // auto-advancing to the next pipeline entry.
  const pipelineAbortedRef = useRef(false);
  // True when the current pipeline is the complete tour (all leaf tours).
  // Drives which finish modal variant to show.
  const isFullTourRef = useRef(false);
  // Flat ordered sequence of all leaf tours from the API — used to find the
  // "next tour" when a partial/singular tour finishes normally.
  const allToursSequenceRef = useRef([]);

  const [activeSteps, setActiveSteps] = useState([]);
  const [activeTourKey, setActiveTourKey] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourRunning, setTourRunning] = useState(false);
  const [canAdvance, setCanAdvance] = useState(true);
  // { key, name } of the NEXT tour in the pipeline, or null (last/solo tour).
  // Drives the "Next: {name}" button label in TourTooltip on the last step.
  const [nextPipelineTour, setNextPipelineTour] = useState(null);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  // { isFullTour: bool, nextTour: { key, name } | null }
  const [finishModalData, setFinishModalData] = useState(null);

  const { controls, on, Tour } = useJoyride({
    continuous: true,
    steps: activeSteps,
    options: {
      skipBeacon: true,
      overlayColor: "rgba(30, 41, 57, 0.72)",
      spotlightPadding: 8,
      spotlightRadius: 8,
      blockTargetInteraction: true,
      dismissKeyAction: false,
      overlayClickAction: false,
      scrollDuration: 400,
      offset: 16,
      scrollOffset: 80,
      zIndex: 10000,
      primaryColor: BRAND.PRIMARY,
    },
    tooltipComponent: TourTooltip,
    arrowComponent: TourArrow,
  });

  // Internal launcher — loads steps and queues a joyride start at fromStep.
  // Does NOT touch pipeline state; callers manage that separately.
  const _launchTour = useCallback(
    (key, fromStep = 0) => {
      const stepsGetter = TOUR_REGISTRY[key];
      if (!stepsGetter) {
        console.warn(`[GuidedTour] No tour registered for key: "${key}"`);
        return;
      }
      const steps = stepsGetter(navigate, store);
      if (!steps?.length) {
        console.warn(`[GuidedTour] Tour "${key}" has no steps defined yet.`);
        return;
      }
      setCanAdvance(true);
      setActiveTourKey(key);
      activeTourKeyRef.current = key;
      setActiveSteps(steps);
      pendingStartRef.current = { fromStep };
    },
    [navigate],
  );

  // Stable ref so the tour:end subscription (set up once on mount) always calls
  // the latest _launchTour without a stale closure.
  const launchTourRef = useRef(_launchTour);
  useEffect(() => { launchTourRef.current = _launchTour; }, [_launchTour]);

  // Subscribe to joyride lifecycle events
  useEffect(() => {
    const unsubChange = on("step:before", ({ index }) => {
      // Restore overlay visibility in case it was hidden by a step's before() hook
      const overlay = document.querySelector(".react-joyride__overlay");
      if (overlay) overlay.style.visibility = "";
      stepRef.current = index;
      setCurrentStep(index);
    });

    const unsubStart = on("tour:start", () => setTourRunning(true));

    const unsubEnd = on("tour:end", () => {
      const aborted = pipelineAbortedRef.current;
      pipelineAbortedRef.current = false; // always reset for next run

      const pipeline = pipelineRef.current;
      const nextIdx = pipelineIdxRef.current + 1;

      if (!aborted && pipeline.length > 0 && nextIdx < pipeline.length) {
        // Advance pipeline — start the next tour
        pipelineIdxRef.current = nextIdx;
        const next = pipeline[nextIdx];
        const afterNext = nextIdx + 1 < pipeline.length ? pipeline[nextIdx + 1] : null;
        setNextPipelineTour(afterNext ? { key: afterNext.key, name: afterNext.name } : null);
        launchTourRef.current(next.key, next.fromStep);
        return;
      }

      // Pipeline exhausted or user aborted — full cleanup
      if (aborted) {
        window.dispatchEvent(new CustomEvent("__tour:aborted", { detail: { key: activeTourKeyRef.current } }));
        setSkipModalOpen(true);
      } else {
        // Tour completed normally — open the appropriate finish modal
        const finishedKey = activeTourKeyRef.current;
        if (isFullTourRef.current) {
          setFinishModalData({ isFullTour: true, nextTour: null });
        } else {
          const seq = allToursSequenceRef.current;
          const idx = seq.findIndex((t) => t.key === finishedKey);
          const next = idx !== -1 && idx + 1 < seq.length ? seq[idx + 1] : null;
          const firstTour = seq.length > 0 ? seq[0] : null;
          setFinishModalData({ isFullTour: false, nextTour: next, firstTour });
        }
        setFinishModalOpen(true);
      }
      isFullTourRef.current = false;
      pipelineRef.current = [];
      pipelineIdxRef.current = 0;
      setNextPipelineTour(null);
      stepRef.current = 0;
      setTourRunning(false);
      setCurrentStep(0);
      setCanAdvance(true);
      setActiveTourKey(null);
      activeTourKeyRef.current = null;
      setActiveSteps([]);
    });

    return () => {
      unsubChange();
      unsubStart();
      unsubEnd();
    };
  }, [on]);

  // Inject a smooth transition on the spotlight so that when a spotlit element
  // changes height (e.g. an API result arrives after the skeleton), the outline
  // glides to the new size instead of snapping.
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-guided-tour-spotlight", "true");
    style.textContent =
      ".react-joyride__spotlight { transition: top 220ms ease, left 220ms ease, width 220ms ease, height 220ms ease !important; }";
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  // Block Escape whenever the tour is running and a MUI backdrop is present.
  //
  // Step-index tracking would have a race: before() opens the menu, but
  // step:before (which updates stepRef) fires only after before() resolves —
  // so stepRef still points to the previous step during the opening window.
  //
  // Using the backdrop's DOM presence instead is race-free: the backdrop mounts
  // the instant avatarBtn/navBtn.click() fires in before(), which is the exact
  // moment Escape protection needs to start. Users cannot open menus themselves
  // during a tour (the overlay blocks clicks), so any MuiBackdrop present is
  // always tour-controlled. Capture phase ensures we run before MUI's listeners.
  useEffect(() => {
    const handler = (e) => {
      if (!tourRunning || e.key !== "Escape") return;
      if (document.querySelector(".MuiBackdrop-root")) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [tourRunning]);

  // When activeSteps state update has propagated (i.e. useJoyride has processed the new steps),
  // fire controls.start(). We defer via pendingStartRef so startTour() stays synchronous.
  useEffect(() => {
    if (!pendingStartRef.current || activeSteps.length === 0) return;
    const { fromStep } = pendingStartRef.current;
    pendingStartRef.current = null;
    const timer = setTimeout(() => controls.start(fromStep), 80);
    return () => clearTimeout(timer);
  }, [activeSteps, controls]);

  // Public: starts a single tour (clears any active pipeline first).
  const startTour = useCallback(
    (key, fromStep = 0) => {
      isFullTourRef.current = false;
      pipelineRef.current = [];
      pipelineIdxRef.current = 0;
      pipelineAbortedRef.current = false;
      setNextPipelineTour(null);
      _launchTour(key, fromStep);
    },
    [_launchTour],
  );

  // Public: starts an ordered pipeline of tours. Each item: { key, fromStep, name }.
  // Pass isFullTour = true when this is the "Take Complete Tour" pipeline so the
  // correct finish modal variant is shown at the end.
  const startPipeline = useCallback(
    (pipeline, isFullTour = false) => {
      if (!pipeline?.length) return;
      isFullTourRef.current = isFullTour;
      pipelineRef.current = pipeline;
      pipelineIdxRef.current = 0;
      pipelineAbortedRef.current = false;
      const second = pipeline.length > 1 ? pipeline[1] : null;
      setNextPipelineTour(second ? { key: second.key, name: second.name } : null);
      const first = pipeline[0];
      _launchTour(first.key, first.fromStep);
    },
    [_launchTour],
  );

  // Called by LearningCenter once tour data loads — populates the flat global
  // sequence used to determine the "next tour" in the partial finish modal.
  const setGlobalTourSequence = useCallback((flatTours) => {
    allToursSequenceRef.current = flatTours ?? [];
  }, []);

  // Signals that the user explicitly exited (skip/close) so tour:end will NOT
  // advance to the next pipeline entry. Clears pipeline state immediately.
  const abortPipeline = useCallback(() => {
    pipelineAbortedRef.current = true;
    pipelineRef.current = [];
    pipelineIdxRef.current = 0;
    setNextPipelineTour(null);
  }, []);

  const openSkipModal = useCallback(() => setSkipModalOpen(true), []);
  const closeSkipModal = useCallback(() => setSkipModalOpen(false), []);
  const closeFinishModal = useCallback(() => setFinishModalOpen(false), []);

  // Safety-net axios interceptor: while prospects or marketing_list tour is active,
  // strip tab and tenant_path from every /person/search POST body before it reaches
  // the network. The app uses axios (→ XHR), so this is the correct interception
  // point — window.fetch patching does not intercept axios calls.
  // The timing fixes in the tour files are the primary defence; this is the backstop.
  useEffect(() => {
    const GUARDED_TOURS = new Set(["prospects", "marketing_list"]);
    if (!GUARDED_TOURS.has(activeTourKey)) return;

    const interceptorId = axiosInstance.interceptors.request.use((config) => {
      if (
        config.url?.includes("/person/search") &&
        config.method?.toLowerCase() === "post" &&
        config.data
      ) {
        try {
          const body =
            typeof config.data === "string"
              ? JSON.parse(config.data)
              : config.data;
          let changed = false;
          if ("tab" in body) { delete body.tab; changed = true; }
          if ("tenant_path" in body) { delete body.tenant_path; changed = true; }
          if (Array.isArray(body.table_columns)) {
            const before = body.table_columns.length;
            body.table_columns = body.table_columns.filter(
              (c) => c !== "tab" && c !== "tenant_path",
            );
            if (body.table_columns.length !== before) changed = true;
          }
          if (changed) {
            config.data =
              typeof config.data === "string"
                ? JSON.stringify(body)
                : body;
          }
        } catch (_) {}
      }
      return config;
    });

    return () => {
      axiosInstance.interceptors.request.eject(interceptorId);
    };
  }, [activeTourKey]);

  // Programmatically stop the active tour (e.g. from a "close" button in the app).
  const stopTour = useCallback(() => {
    abortPipeline();
    controls?.stop?.();
    stepRef.current = 0;
    setTourRunning(false);
    setCurrentStep(0);
    setCanAdvance(true);
    setActiveTourKey(null);
    setActiveSteps([]);
  }, [controls, abortPipeline]);

  return (
    <TourContext.Provider
      value={{
        tourRunning,
        activeTourKey,
        currentStep,
        stepRef,
        controls,
        canAdvance,
        setCanAdvance,
        startTour,
        startPipeline,
        nextPipelineTour,
        abortPipeline,
        stopTour,
        skipModalOpen,
        openSkipModal,
        closeSkipModal,
        finishModalOpen,
        finishModalData,
        closeFinishModal,
        setGlobalTourSequence,
      }}
    >
      {Tour}
      <TourSkipModal />
      <TourFinishModal />
      {children}
    </TourContext.Provider>
  );
}
